/**
 * Authenticated API client.
 * All methods attach the Better Auth session JWT as a Bearer token.
 * Import `useApiClient` in React components; the hook returns a bound client.
 */
import { useAuth } from '../context/AuthContext';
import type { PainDetail } from '../pages/onboardingMapping';
import type { FitReport } from '../components/fitresults/types';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? 'http://localhost:5000';

/**
 * Error carrying the HTTP status alongside the server's `detail` message.
 * Extends Error, so existing `catch (e: Error) => e.message` handlers are
 * unaffected — but callers that need to branch on 403 no longer have to
 * pattern-match the message text.
 */
export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// ── Types ────────────────────────────────────────────────────────────────────

export interface UserOut {
  id: string;
  name: string;
  email: string;
  role: 'user' | 'admin';
  onboarding_status: 'not_started' | 'in_progress' | 'plan_generated' | 'active';
  subscription_tier: 'free' | 'pro';
  subscription_status: string;
  stripe_customer_id: string | null;
  created_at: string;
}

export interface BikeProfileUpdate {
  bike_type?: 'road' | 'mountain' | 'gravel' | 'trekking' | 'time-trial';
  experience_level?: 'beginner' | 'intermediate' | 'advanced';
  flexibility_level?: 'low' | 'moderate' | 'high';
  position_preference?: 'aggressive' | 'moderate' | 'relaxed';
  pain_points?: string;
  pain_detail?: PainDetail;
  height_cm?: number;
  weight_kg?: number;
  units?: 'metric' | 'imperial';
}

export interface BikeProfileOut extends BikeProfileUpdate {
  id: string;
  user_id: string;
  updated_at: string;
}

export interface RiderProfileUpdate {
  weight_kg?: number;
  height_cm?: number;
  age?: number;
  gender?: string;
  max_hr?: number;
  resting_hr?: number;
  ftp_watts?: number;
  lthr?: number;
  experience_level?: string;
  weekly_hours_available?: number;
  available_days?: string[];
  primary_goal?: string;
  goal_event?: string;
  goal_event_date?: string;
  bike_type?: string;
  has_power_meter?: boolean;
  has_hrm?: boolean;
}

export interface RiderProfileOut extends RiderProfileUpdate {
  id: string;
  user_id: string;
  updated_at: string;
}

export interface UserSettingsUpdate {
  timezone?: string;
  units?: 'metric' | 'imperial';
  language?: string;
  notification_preferences?: Record<string, unknown>;
}

export interface UserSettingsOut extends UserSettingsUpdate {
  id: string;
  user_id: string;
}

export interface OnboardingStatusOut {
  onboarding_status: UserOut['onboarding_status'];
  has_bike_profile: boolean;
  has_rider_profile: boolean;
}

export interface CheckoutSessionOut {
  checkout_url: string;
}

export interface BillingPortalOut {
  portal_url: string;
}

export interface BikeFitReportOut {
  order_id: string;
  created_at: string;
  completed_at: string | null;
  report_url: string | null;
}

export type Plan = 'monthly' | 'yearly';

export interface SubscriptionOut {
  source: 'stripe' | 'admin_comp';
  plan: Plan | null;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

export interface PriceItemOut {
  amount: number;
  currency: string;
  type: 'one_time' | 'recurring';
  interval?: string;
}

export interface PricesOut {
  monthly: PriceItemOut;
  yearly: PriceItemOut;
}

export interface BillingStatusOut {
  role: 'user' | 'admin';
  subscription_tier: 'free' | 'pro';
  subscription_status: string;
  stripe_customer_id: string | null;
  has_bike_fit: boolean;
  has_unused_credit: boolean;
  can_view_recommendations: boolean;
  is_entitled: boolean;
  subscription: SubscriptionOut | null;
  completed_reports: BikeFitReportOut[];
}

export interface AdminUserOut {
  id: string;
  name: string;
  email: string;
  role: 'user' | 'admin';
  created_at: string;
  onboarding_status: 'not_started' | 'in_progress' | 'plan_generated' | 'active';
  subscription_tier: 'free' | 'pro';
  has_unused_credit: boolean;
  completed_reports_count: number;
  last_purchase_at: string | null;
  access: 'monthly' | 'yearly' | 'comp' | null;
}

export interface AdminSubscriptionOut {
  id: string;
  source: 'stripe' | 'admin_comp';
  plan: 'monthly' | 'yearly' | null;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  created_at: string;
  stripe_subscription_id: string | null;
}

export interface AdminStatsOut {
  total_users: number;
  onboarded_users: number;
  users_with_report: number;
  signups_7d: number;
  signups_30d: number;
  subscribers_monthly: number;
  subscribers_yearly: number;
  mrr_cents: number;
  new_subscriptions_7d: number;
  new_subscriptions_30d: number;
  cancellations_7d: number;
  cancellations_30d: number;
  past_due: number;
  comped_active: number;
  legacy_credits_outstanding: number;
}

export interface AdminOrderOut {
  id: string;
  status: 'purchased' | 'completed';
  source: 'stripe' | 'admin_grant' | 'subscription';
  created_at: string;
  completed_at: string | null;
  fit_score: number | null;
  stripe_session_id: string | null;
  report_file_path: string | null;
}

export interface AdminUserDetailOut extends AdminUserOut {
  email_verified: boolean;
  updated_at: string;
  stripe_customer_id: string | null;
  subscription_status: string;
  providers: string[];
  orders: AdminOrderOut[];
  subscriptions: AdminSubscriptionOut[];
}

export interface ReportGenerationRequest {
  session_data: Record<string, unknown>;
  annotated_image_urls?: Record<string, string>;
  recommendations_text?: string;
  frame_images?: { bdc?: string; tdc?: string };
}

export interface ReportGenerationResponse {
  order_id: string;
  report_url: string;
  status: string;
}

export interface ReportListItem {
  order_id: string;
  created_at: string;
  completed_at: string | null;
  report_url: string | null;
}

// ── Client factory ────────────────────────────────────────────────────────────

function makeClient(getToken: () => Promise<string | null>) {
  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> {
    const token = await getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(`${BACKEND_URL}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!res.ok) {
      let detail = `API error ${res.status}`;
      try {
        const data = await res.json();
        if (data.detail) detail = data.detail;
      } catch { /* ignore */ }
      throw new ApiError(res.status, detail);
    }

    if (res.status === 204) return undefined as unknown as T;
    return res.json() as Promise<T>;
  }

  return {
    // Auth
    getMe: () => request<UserOut>('GET', '/api/auth/me'),

    // Onboarding
    getOnboardingStatus: () => request<OnboardingStatusOut>('GET', '/api/onboarding/status'),
    saveBikeProfile: (data: BikeProfileUpdate) =>
      request<BikeProfileOut>('POST', '/api/onboarding/bike-profile', data),
    saveRiderProfile: (data: RiderProfileUpdate) =>
      request<RiderProfileOut>('POST', '/api/onboarding/rider-profile', data),

    // Profile
    getBikeProfile: () => request<BikeProfileOut>('GET', '/api/profile/bike'),
    updateBikeProfile: (data: BikeProfileUpdate) =>
      request<BikeProfileOut>('PATCH', '/api/profile/bike', data),
    getRiderProfile: () => request<RiderProfileOut>('GET', '/api/profile/rider'),
    updateRiderProfile: (data: RiderProfileUpdate) =>
      request<RiderProfileOut>('PATCH', '/api/profile/rider', data),

    // Settings
    getSettings: () => request<UserSettingsOut>('GET', '/api/settings'),
    updateSettings: (data: UserSettingsUpdate) =>
      request<UserSettingsOut>('PATCH', '/api/settings', data),

    // Billing
    createSubscriptionCheckout: (plan: Plan) =>
      request<CheckoutSessionOut>('POST', '/api/billing/create-subscription-checkout-session', { plan }),
    getPrices: () => request<PricesOut>('GET', '/api/billing/prices'),
    openBillingPortal: () =>
      request<BillingPortalOut>('POST', '/api/billing/portal'),
    getBillingStatus: () => request<BillingStatusOut>('GET', '/api/billing/status'),

    // Reports
    getFitReport: (sessionData: Record<string, unknown>) =>
      request<FitReport>('POST', '/api/fit-report', { session_data: sessionData }),
    generateReport: (data: ReportGenerationRequest) =>
      request<ReportGenerationResponse>('POST', '/api/generate-report', data),
    getReports: () => request<ReportListItem[]>('GET', '/api/reports'),

    // Account
    deleteAccount: () => request<void>('DELETE', '/api/account'),
    exportData: () => request<unknown>('GET', '/api/account/export'),

    // Admin
    getAdminUsers: () => request<AdminUserOut[]>('GET', '/api/admin/users'),
    getAdminStats: () => request<AdminStatsOut>('GET', '/api/admin/stats'),
    getAdminUserDetail: (userId: string) =>
      request<AdminUserDetailOut>('GET', `/api/admin/users/${encodeURIComponent(userId)}`),
    grantCredit: (userId: string) =>
      request<AdminOrderOut>('POST', `/api/admin/users/${encodeURIComponent(userId)}/grant-credit`),
    revokeOrder: (orderId: string) =>
      request<void>('DELETE', `/api/admin/orders/${encodeURIComponent(orderId)}`),
    compAccess: (userId: string, endsAt: string) =>
      request<AdminSubscriptionOut>('POST', `/api/admin/users/${encodeURIComponent(userId)}/comp-access`, { ends_at: endsAt }),
    endComp: (subscriptionId: string) =>
      request<void>('DELETE', `/api/admin/comps/${encodeURIComponent(subscriptionId)}`),
  };
}

// ── React hook ────────────────────────────────────────────────────────────────

export function useApiClient() {
  const { getToken } = useAuth();
  return makeClient(getToken);
}
